import { PublicRouteLink } from '../public-route-bridge';
import { rentalUnitPath, type PublicRentalApplicationUnitLocationState } from '../public-route-contracts';
import { dateFit, formatRent, parseIsoDate, unitFacts, unitPhotos, unitTitle, type DateFit, type PublicListingUnit } from './listing-model';
import shell from '../shell/shell.module.css';
import styles from './listing.module.css';

/** The unit snapshot the listing has always handed to the apply route through `location.state`. */
export function unitLocationState(unit: PublicListingUnit, organizationName?: string): PublicRentalApplicationUnitLocationState {
  return {
    publicUnitKey: unit.publicUnitKey,
    propertyName: unit.propertyName,
    unitName: unit.unitName,
    addressLine1: unit.address1,
    addressLine2: unit.address2,
    city: unit.city,
    state: unit.state,
    postalCode: unit.postalCode,
    bedrooms: unit.bedrooms,
    bathrooms: unit.bathrooms,
    areaSqft: unit.areaSqft,
    rent: unit.rent,
    availableFrom: unit.availableFrom ?? undefined,
    applyEligible: unit.applyEligible,
    organizationName: organizationName || undefined,
    photoUrls: unit.photoUrls,
    coverImageUrl: unit.coverImageUrl,
    description: unit.description,
  };
}

export function FitChip({ fit }: { fit: DateFit }) {
  return (
    <span className={styles.fit} data-tone={fit.tone}>
      {fit.tone !== 'none' ? <i aria-hidden="true" /> : null}
      <span className={styles.num}>{fit.label}</span>
      {fit.detail ? <small>· {fit.detail}</small> : null}
    </span>
  );
}

export function ListingCard({
  unit,
  organizationSlug,
  organizationName,
  moveIn,
  today,
  size = 'regular',
  eager = false,
}: {
  unit: PublicListingUnit;
  organizationSlug: string;
  organizationName: string;
  moveIn: string | null;
  today: Date;
  size?: 'regular' | 'big';
  eager?: boolean;
}) {
  const fit = dateFit(unit, parseIsoDate(moveIn), today);
  const cover = unitPhotos(unit)[0];
  const title = unitTitle(unit);
  const rent = formatRent(unit.rent);
  const facts = unitFacts(unit);
  const content = (
    <>
      <span className={styles.cardPhoto}>
        {cover ? <img src={cover} alt="" loading={eager ? 'eager' : 'lazy'} decoding="async" draggable={false} /> : null}
        <FitChip fit={fit} />
      </span>
      <span className={styles.cardBody}>
        <span className={styles.cardText}>
          {rent ? (
            <span className={styles.rent}>
              <span className={styles.num}>{rent}</span>
              <small> / mo{size === 'big' ? <span className={styles.bigOnly}>nth</span> : null}</small>
            </span>
          ) : null}
          <span className={styles.cardTitle}>{title}</span>
          {facts ? <span className={`${styles.cardFacts} ${styles.num}`}>{facts}</span> : null}
        </span>
        {size === 'big' ? <span className={`${shell.btn} ${shell.btnBrand} ${shell.btnSmall} ${styles.seeHome}`} aria-hidden="true">See home</span> : null}
      </span>
    </>
  );

  if (!unit.publicUnitKey) return <article className={styles.card} data-size={size}>{content}</article>;

  return (
    <PublicRouteLink
      to={rentalUnitPath(organizationSlug, unit.publicUnitKey, moveIn)}
      state={{ publicRentalApplicationUnit: unitLocationState(unit, organizationName) }}
      className={styles.card}
      data-size={size}
      aria-label={[title, rent ? `${rent} a month` : null, fit.label].filter(Boolean).join(', ')}
    >
      {content}
    </PublicRouteLink>
  );
}

/**
 * Five or more homes on a wide screen open with the best fit large beside a 2×2; the rest follow
 * in a uniform grid. Fewer homes, or narrower screens, use the uniform grid alone.
 */
export function ListingResults({
  units,
  organizationSlug,
  organizationName,
  moveIn,
  today,
}: {
  units: PublicListingUnit[];
  organizationSlug: string;
  organizationName: string;
  moveIn: string | null;
  today: Date;
}) {
  const bento = units.length >= 5;
  const head = bento ? units.slice(0, 5) : [];
  const rest = bento ? units.slice(5) : units;
  const card = (unit: PublicListingUnit, index: number, size: 'regular' | 'big' = 'regular') => (
    <ListingCard
      key={unit.publicUnitKey ?? `${unit.propertyName}-${unit.unitName}-${index}`}
      unit={unit}
      organizationSlug={organizationSlug}
      organizationName={organizationName}
      moveIn={moveIn}
      today={today}
      size={size}
      eager={index < 6}
    />
  );

  return (
    <div className={styles.results}>
      {bento ? <div className={styles.bento}>{head.map((unit, index) => card(unit, index, index === 0 ? 'big' : 'regular'))}</div> : null}
      {rest.length > 0 ? <div className={styles.grid}>{rest.map((unit, index) => card(unit, index + head.length))}</div> : null}
    </div>
  );
}
